import { DownOutlined } from "@ant-design/icons"
import { Button, Card, type TablePaginationConfig } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { type ReactNode, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../../../../components/EntityTable/surfaces.ts"
import { TableAddAction } from "../../../../../../../components/Table/TableAddAction.tsx"
import { TableResultCount } from "../../../../../../../components/Table/TableResultCount.tsx"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import { useScrolledTable } from "../../../../../../../hooks/use-scrolled-table.ts"
import { cn } from "../../../../../../../utils/cn.ts"
import { type PermissionProjectPermissions } from "../../../../../../SettingsPermissions/types/permissions.types.ts"
import { type ProjectMilestoneFilterOptions } from "../../../../../redux/project.types.ts"
import { type MilestoneBulkResult } from "../../../../../redux/project-milestones/project-milestones.types.ts"
import { milestoneStatusOptions } from "../../../../constants/milestone-statuses.ts"
import { calculateEffectiveSchedule } from "../../../../utils/milestone-effective-schedule.ts"
import { useAccountToday } from "../../../../utils/milestone-schedule-state.ts"
import type { MilestoneDependency } from "../../../../../types/project-milestone-dependency.types.ts"
import type {
  ProjectMilestone,
  ProjectMilestoneFinanceItem
} from "../../../../../types/project-phases.types.tsx"
import { ProjectViewMilestoneFinanceItems } from "../ProjectViewMilestoneFinanceItems/ProjectViewMilestoneFinanceItems.tsx"
import {
  MilestonesBulkActionBar,
  type MilestoneBulkActionParams
} from "./MilestonesBulkActionBar.tsx"
import { ProjectViewMilestoneNewRow } from "./ProjectViewMilestoneNewRow.tsx"
import { MilestoneSortableRow } from "./ProjectViewMilestonesSortableRow.tsx"
import { getColumns } from "./columns.tsx"

interface RowProps
  extends
    React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLTableRowElement>,
      HTMLTableRowElement
    >,
    Pick<ProjectViewMilestonesListProps, "onSelectRow"> {
  record: ProjectMilestone
  children?: ReactNode[]
}

interface ProjectViewMilestonesListProps {
  userId: string
  showFinanceItems: boolean
  projectPermissions: PermissionProjectPermissions
  milestones: ProjectMilestone[]
  dependencies: MilestoneDependency[]
  editableRow: string
  projectId: string
  /** The project's current contract totals — the same figures the header
   * and the Financial Alerts panel read, so a row's Receivables/Payables
   * flag red against the identical source of truth. */
  contractValue: number | null
  contractExpenses: number | null
  fetching: boolean
  next?: string | null
  /** The page a load-more failed on, offered back. */
  retryNext?: string | null
  count: number
  /** The most rows "Load all" asks for in one request. */
  loadAllCap?: number
  onLoadAll?: () => void
  options: ProjectMilestoneFilterOptions
  financeOptions: Record<string, BaseOptionType[]>
  createAllowed?: boolean
  createDisabled?: boolean
  selectedIds: string[]
  onSelectedIdsChange: (ids: string[]) => void
  onBulkAction: (
    params: MilestoneBulkActionParams
  ) => Promise<MilestoneBulkResult>
  onAddMilestone: () => void
  onSelectRow: (record: ProjectMilestone, action: string) => void
  onPageChange: (page: number) => void
  onLoadMore: () => void
  onDeleteMilestoneFinanceItem: (
    id: string,
    milestone: ProjectMilestone
  ) => Promise<void>
  onUpdateMilestoneFinanceItem: (
    itemId: string,
    data: Partial<ProjectMilestoneFinanceItem>,
    milestone: ProjectMilestone
  ) => Promise<void>
  onAddMilestoneFinanceItem: (
    data: Partial<ProjectMilestoneFinanceItem>,
    milestone: ProjectMilestone
  ) => Promise<boolean>
  onReorderMilestones: (orderedIds: string[]) => void
  // False only for a tour/demo preview reusing this real table on fake data —
  // its column edits stay local instead of overwriting every real project's
  // saved Milestones layout, which is keyed by this same surface for every
  // account. Defaults to true so every real caller is unaffected.
  persistColumnConfig?: boolean
}

export const ProjectViewMilestonesList = ({
  userId,
  showFinanceItems,
  projectPermissions,
  milestones,
  dependencies,
  editableRow,
  fetching,
  projectId,
  contractValue,
  contractExpenses,
  next,
  retryNext,
  count,
  loadAllCap,
  onLoadAll,
  options,
  financeOptions,
  createAllowed,
  createDisabled,
  selectedIds,
  onSelectedIdsChange,
  onBulkAction,
  onAddMilestone,
  onLoadMore,
  onSelectRow,
  onPageChange,
  onDeleteMilestoneFinanceItem,
  onUpdateMilestoneFinanceItem,
  onAddMilestoneFinanceItem,
  onReorderMilestones,
  persistColumnConfig = true
}: ProjectViewMilestonesListProps) => {
  const { t } = useTranslation()
  const { parseDate, userFormat } = useDateFormat()
  const today = useAccountToday()
  const isLocked = !!editableRow
  const moduleDocs = projectPermissions?.docs
  const moduleAccess = projectPermissions?.milestones
  const moduleFinance = projectPermissions?.planned_finances

  const deleteAllowed = moduleAccess?.project_milestone_delete
  const editAllowed = moduleAccess?.project_milestone_edit
  const deleteFileAllowed = moduleDocs?.project_doc_delete
  const uploadFileAllowed = moduleDocs?.project_doc_create
  const deleteForeignFileAllowed =
    deleteFileAllowed && moduleDocs?.project_doc_manage_others

  const reorderAllowed = !!editAllowed && milestones?.length > 1

  const [bulkBarVisible, setBulkBarVisible] = useState(false)

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedIds,
      preserveSelectedRowKeys: true as const,
      onChange: (keys: React.Key[]) => onSelectedIdsChange(keys.map(String)),
      // A selection cannot be edited while a row is in inline edit; the ticks
      // it already holds stay visible, they just cannot change.
      getCheckboxProps: () => ({ disabled: isLocked })
    }),
    [selectedIds, onSelectedIdsChange, isLocked]
  )

  const bulkStatusOptions = milestoneStatusOptions(t)

  const milestoneNames = useMemo(
    () =>
      Object.fromEntries(
        (milestones ?? []).map((milestone) => [milestone.id, milestone.name])
      ),
    [milestones]
  )

  const sortableIds = useMemo(
    () => milestones?.map((m) => m?.id) || [],
    [milestones]
  )

  // Memoised: a fresh object here changes the table's `components` identity on
  // every render, which remounts the body mid-drag.
  const rowSortable = useMemo(
    () =>
      reorderAllowed
        ? {
            items: sortableIds,
            onReorder: onReorderMilestones,
            handleLabel: t("projectMilestones.actions.reorderHandle")
          }
        : undefined,
    [reorderAllowed, sortableIds, onReorderMilestones, t]
  )

  // The same scheduling engine the Gantt renders from — a milestone whose
  // stored Planned Start/End would violate a dependency shows its
  // recalculated dates here too, so the two views never disagree.
  const effectiveSchedule = useMemo(
    () => calculateEffectiveSchedule({ milestones, dependencies, parseDate }),
    [milestones, dependencies, parseDate]
  )

  const columns = useMemo(
    () =>
      getColumns({
        t,
        currentUserId: userId,
        editAllowed,
        deleteAllowed,
        uploadFileAllowed,
        deleteForeignFileAllowed,
        deleteFileAllowed,
        projectId,
        isLocked,
        effectiveSchedule,
        userFormat,
        milestoneNames,
        dependencies,
        today,
        parseDate,
        onSelectRow,
        contractValue,
        contractExpenses
      }),
    [
      userId,
      projectId,
      isLocked,
      uploadFileAllowed,
      deleteFileAllowed,
      deleteForeignFileAllowed,
      effectiveSchedule,
      userFormat,
      milestoneNames,
      dependencies,
      today,
      parseDate,
      onSelectRow,
      contractValue,
      contractExpenses,
      editAllowed,
      deleteAllowed,
      t
    ]
  )

  const onChangeTable = useCallback(
    (pagination: TablePaginationConfig) => {
      onPageChange(pagination.current || 1)
    },
    [onPageChange]
  )

  const RowComponent = useCallback(
    ({ record, onSelectRow, ...props }: RowProps) => {
      // Editing existing milestone
      if (record?.id === editableRow && editableRow) {
        return (
          <ProjectViewMilestoneNewRow
            options={options}
            mode="edit"
            record={record}
            effectiveEntry={effectiveSchedule.get(record.id)}
            onSelectRow={onSelectRow}
            {...props}
          />
        )
      }

      // Normal row display
      if (reorderAllowed && record?.id) {
        return <MilestoneSortableRow id={record.id} {...props} />
      }

      return <tr {...props}>{props?.children}</tr>
    },
    [editableRow, options, reorderAllowed, effectiveSchedule]
  )

  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading: fetching,
    next: next,
    loadMore: onLoadMore,
    disabled: isLocked,
    // The bulk bar is inside the measured card, so its height comes off the
    // body: on a short screen a selection would otherwise leave two rows.
    minRows: 3
  })

  return (
    <TableAddAction
      label={t("table.addRow.milestone")}
      // The bulk bar takes the corner the "+" is anchored to for as long as it
      // is up — which outlasts the selection, since it goes on reporting the
      // outcome after one.
      allowed={bulkBarVisible ? false : !!createAllowed}
      disabled={!!createDisabled}
      onAdd={onAddMilestone}
    >
      <Card classNames={{ body: "p-0" }} ref={tableRef}>
        <EntityTable<ProjectMilestone>
          surface={TABLE_SURFACES.milestones}
          persistColumnConfig={persistColumnConfig}
          selectionMode="on-demand"
          rowSelection={rowSelection}
          tab={DEFAULT_TAB}
          disabled={isLocked}
          rowSortable={rowSortable}
          withOutPagination
          rowKey="id"
          size="small"
          rowHoverable={false}
          onScroll={onTableScroll}
          className={cn(
            "[&_th]:bg-primary [&_th]:rounded-none [&_th]:text-white",
            {
              "[&_.ant-table-cell-scrollbar]:w-0 [&_.ant-table-cell-scrollbar]:p-1":
                !milestones?.length
            }
          )}
          columns={columns}
          scroll={{
            x: "max-content",
            y: tableHeight
          }}
          onChange={onChangeTable}
          dataSource={milestones}
          loading={fetching && !milestones?.length}
          pagination={false}
          onRow={(record) => ({
            record,
            onSelectRow,
            onDoubleClick: editAllowed
              ? (e) => {
                  const target = e.target as HTMLElement
                  if (
                    isLocked ||
                    target.closest(
                      "button, a, .ant-btn, .ant-table-selection-column"
                    )
                  )
                    return

                  onSelectRow(record, "edit_row")
                }
              : undefined
          })}
          components={{
            body: editAllowed && milestones?.length ? { row: RowComponent } : {}
          }}
          {...(showFinanceItems
            ? {
                expandable: {
                  fixed: "left",
                  expandIcon: ({ expanded, onExpand, record }) => (
                    <Button
                      disabled={isLocked}
                      icon={<DownOutlined />}
                      onClick={(e) => onExpand(record, e)}
                      className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                    />
                  ),
                  expandedRowRender: (record) => (
                    <ProjectViewMilestoneFinanceItems
                      userId={userId}
                      milestone={record}
                      projectId={projectId}
                      disabled={isLocked}
                      options={financeOptions}
                      //
                      deleteAllowed={
                        moduleFinance?.project_planned_finance_delete
                      }
                      editAllowed={moduleFinance?.project_planned_finance_edit}
                      createAllowed={
                        moduleFinance?.project_planned_finance_create
                      }
                      uploadFileAllowed={uploadFileAllowed}
                      deleteFileAllowed={deleteFileAllowed}
                      deleteForeignFileAllowed={deleteForeignFileAllowed}
                      //
                      onAdd={onAddMilestoneFinanceItem}
                      onUpdate={onUpdateMilestoneFinanceItem}
                      onDelete={onDeleteMilestoneFinanceItem}
                      loading={false}
                    />
                  )
                }
              }
            : {})}
        />
        <TableResultCount
          underAddAction
          loaded={milestones?.length ?? 0}
          total={count}
          loading={fetching}
          next={next}
          retryNext={retryNext}
          onRetry={onLoadMore}
          action={
            onLoadAll &&
            (next || retryNext) &&
            (milestones?.length ?? 0) < Math.min(count, loadAllCap ?? count) ? (
              <Button
                type="link"
                size="small"
                disabled={fetching || isLocked}
                onClick={onLoadAll}
              >
                {count > (loadAllCap ?? count)
                  ? t("projectMilestones.loadFirst", { count: loadAllCap })
                  : t("projectMilestones.loadAll", { count })}
              </Button>
            ) : null
          }
        />
        <MilestonesBulkActionBar
          selectedIds={selectedIds}
          milestoneNames={milestoneNames}
          statusOptions={bulkStatusOptions}
          editAllowed={editAllowed}
          deleteAllowed={deleteAllowed}
          onClear={() => onSelectedIdsChange([])}
          onAction={onBulkAction}
          onVisibleChange={setBulkBarVisible}
          dateFormat={userFormat}
        />
      </Card>
    </TableAddAction>
  )
}
